import dayjs from "dayjs"
import { defaultActionFlowConfig, defaultCatchAllFlow, flowTypes } from "../config"
import { AssociationSettings, DisciplineRecord, Profile } from "../models"
import { createNewBatch } from "../providers/endpointDataProvider/asyncTasks"
import { createBulkDisciplineActon, updateDisciplinaryData } from "../providers/endpointDataProvider/dataSync"
import {
  AssociationId,
  AsyncTaskTypesEnum,
  AuthCredentials,
  BulkDisciplineActionSchema,
  DisciplineActionsEnum,
  Email,
  ProcessType,
  SegmentInfo,
  UnixMsString,
} from "../types"
import { ConfigService } from "./configService"
import { SessionService } from "./sessionService"
import { getBulkDisciplineActions, putBulkDisciplineActions } from "../providers/endpointDataProvider/discipline"
import { ensureStringFormat, removeUnderScores, toValidPfDate } from "../utils"
class DisciplineService {
  async updateBulkDisciplineList(
    idPassport: string,
    offence: any,
    action: any,
    selectedAssociation: string,
    selectedRoles: any,
    authCredentials: AuthCredentials,
  ): Promise<void> {
    try {
      const { username, password } = authCredentials
      const token = await SessionService.prepareAuthTokens(username, password)
      await createBulkDisciplineActon(idPassport, offence, action, selectedAssociation, selectedRoles, token)
    } catch (error) {
      throw error
    }
  }

  async uploadDisciplinaryActions(
    selectedAssociation: string,
    disciplinaryActionsTracker: any,
    authCredentials: AuthCredentials,
  ): Promise<void> {
    try {
      const { username, password } = authCredentials
      const token = await SessionService.prepareAuthTokens(username, password)
      await updateDisciplinaryData(selectedAssociation, disciplinaryActionsTracker, token)
    } catch (error) {
      throw error
    }
  }

  async generateDisciplineReport(
    association: AssociationId,
    timeRange: { fromUnixMs: UnixMsString; toUnixMs: UnixMsString },
    targetEmails: Email[],
    authCredentials: AuthCredentials,
  ): Promise<void> {
    try {
      const { username, password } = authCredentials
      const token = await SessionService.prepareAuthTokens(username, password)
      await createNewBatch(
        association,
        { timeRange, targetEmails, asyncTaskType: AsyncTaskTypesEnum.DISCIPLINE_REPORT },
        token,
      )
    } catch (error) {
      throw error
    }
  }

  checkIfActionTaken = (actionFlow: DisciplineRecord[], targetAction: string) => {
    for (const disciplineRecord of actionFlow) {
      if (disciplineRecord.getRawData().action === targetAction && disciplineRecord.checkIfActioned()) {
        return true
      }
    }
    return false
  }

  generateCatchAllFlow = (chronologicalRecords: DisciplineRecord[]) => {
    //Records already ordered chronologically
    let maybeCatchAllRecords = [] as DisciplineRecord[]
    for (const historyRecord of chronologicalRecords) {
      const action = historyRecord.getAction()
      const process = historyRecord.getProcess()
      const offence = historyRecord.getOffence()
      if (action === DisciplineActionsEnum.TERMINATE || process === ProcessType.terminate) {
        return []
      }
      if (defaultCatchAllFlow.includes(action)) {
        maybeCatchAllRecords.push(historyRecord)
      }
      if (action === DisciplineActionsEnum.ADD_CATCH_ALL) {
        return [...defaultCatchAllFlow].map((defaultAction) => {
          let uiFlowRecord = new DisciplineRecord({
            rawData: { offence, action: defaultAction },
            uiData: { isPrescribedAction: true, flowType: flowTypes.catchAllFlow },
          })
          uiFlowRecord =
            maybeCatchAllRecords.find((flowRecord) => flowRecord.getAction() === defaultAction) || uiFlowRecord
          return uiFlowRecord
        })
      }
    }
    return []
  }

  buildOffenceActionFlow = (
    historyRecords: DisciplineRecord[],
    defaultFlow: DisciplineActionsEnum[],
    prescribedFlow: DisciplineActionsEnum[],
    isPaused: boolean,
    offence: string,
  ) => {
    return [...defaultFlow].map((action) => {
      const isPrescribedAction = prescribedFlow.includes(action)
      let uiFlowRecord = new DisciplineRecord({ rawData: { offence, action, uiData: { isPrescribedAction } } })
      uiFlowRecord = historyRecords.find((flowRecord) => flowRecord.getRawData().action === action) || uiFlowRecord
      console.log("uiFlowRecord", uiFlowRecord)
      uiFlowRecord.upsertUiData({ isPaused, isPrescribedAction, flowType: "actionFlow" })
      return uiFlowRecord
    })
  }

  generateActiveActionFlows = (
    chronologicalRecords: DisciplineRecord[],
    catchAllExists: boolean,
    configService: ConfigService,
    associationEntity: AssociationSettings,
    generalData: Record<string, any>,
    cohort: string,
  ): Record<string, DisciplineRecord[]> => {
    try {
      let activeActionFlows = {} as Record<string, any>
      let categorisedOffenceRecords = {} as Record<string, DisciplineRecord[]>
      for (const historyRecord of chronologicalRecords) {
        const offence = historyRecord.getOffence()
        const action = historyRecord.getAction()
        const process = historyRecord.getProcess()
        if (action === DisciplineActionsEnum.TERMINATE || process === ProcessType.terminate) {
          break
        }
        const { expiry } = historyRecord.getRawData()
        const isExpired = dayjs().isAfter(dayjs(expiry))
        if (isExpired) {
          continue
        }
        if (!categorisedOffenceRecords[offence]) {
          categorisedOffenceRecords[offence] = []
        }
        if (!offence) {
          continue
        }
        categorisedOffenceRecords[offence].unshift(historyRecord)
      }

      Object.keys(categorisedOffenceRecords).forEach((offence) => {
        let segmentInfo = { ...generalData, offence } as SegmentInfo
        if (cohort) {
          segmentInfo.division = cohort
        }
        const prescribedFlow = associationEntity.getConfiguredProcessInfo(
          "actionFlow",
          ProcessType.discipline,
          segmentInfo,
          configService,
          [],
        )
        activeActionFlows[offence] = this.buildOffenceActionFlow(
          categorisedOffenceRecords[offence],
          defaultActionFlowConfig,
          prescribedFlow,
          catchAllExists,
          offence,
        )
      })
      return activeActionFlows
    } catch (error) {
      console.error("generateActiveActionFlows", { error }, true)
      return {}
    }
  }

  loadBulkDisciplineTableData = async (association: string, authCredentials: AuthCredentials) => {
    const buildTableColumnConfig = () => {
      return [
        { key: "name", label: "Name", sizeFactor: 1 },
        { key: "surname", label: "Surname", sizeFactor: 1 },
        { key: "idPassport", label: "ID/Passport", sizeFactor: 1 },
        { key: "offence", label: "Offence", sizeFactor: 2 },
        { key: "action", label: "Action", sizeFactor: 2 },
        { key: "expiryDate", label: "Expiry Date", sizeFactor: 2 },
        { key: "creationDate", label: "Creation Date", sizeFactor: 2 },
        { key: "lastModifiedDate", label: "Last Modified Date", sizeFactor: 2 },
      ]
    }
    const convertToRenderFormat = (bulkActionTask: BulkDisciplineActionSchema) => {
      const { expiry, creationUnixMs, lastModifiedUnixMs, isComplete, isExpired } = bulkActionTask
      const expiryDate = toValidPfDate(expiry) || "-"
      const creationDate = toValidPfDate(creationUnixMs) || "-"
      const lastModifiedDate = lastModifiedUnixMs ? toValidPfDate(lastModifiedUnixMs) || "-" : "-"

      // Determine status
      let status = "IN PROGRESS"
      if (isComplete) {
        status = "COMPLETE"
      } else if (isExpired) {
        status = "EXPIRED"
      }

      // Replace any empty string fields with "-"
      const converted = { ...bulkActionTask, expiryDate, creationDate, lastModifiedDate, status }

      const result: any = {}
      for (const key in converted) {
        let stringValue = ensureStringFormat(converted[key as keyof typeof converted])

        // Remove underscores from offence and action fields for display
        if (key === "offence" || key === "action") {
          stringValue = removeUnderScores(stringValue)
        }

        result[key] = stringValue === "" ? "-" : stringValue
      }

      // Preserve boolean flags as actual booleans (not strings)
      result.isComplete = isComplete
      result.isExpired = isExpired

      return result
    }
    const { username, password } = authCredentials
    const token = await SessionService.prepareAuthTokens(username, password)
    const bulkActionTasks = await getBulkDisciplineActions(association, token)
    return { tableData: bulkActionTasks.map(convertToRenderFormat), tableColumnConfig: buildTableColumnConfig() }
  }

  addActionFlow = (
    activeActionFlows: Record<string, DisciplineRecord[]>,
    offence: string,
    contextData: Record<string, any>,
    configService: ConfigService,
    associationEntity: AssociationSettings,
    cohort: string,
  ) => {
    let segmentInfo = { ...contextData, offence } as SegmentInfo
    if (cohort) {
      segmentInfo.division = cohort
    }
    const prescribedFlow = associationEntity.getConfiguredProcessInfo(
      "actionFlow",
      ProcessType.discipline,
      segmentInfo,
      configService,
      [],
    )
    const isPaused = false
    activeActionFlows[offence] = this.buildOffenceActionFlow(
      [],
      defaultActionFlowConfig,
      prescribedFlow,
      isPaused,
      offence,
    )
    return activeActionFlows
  }

  getNextPrescribedAction = (
    activeActionFlows: Record<string, DisciplineRecord[]>,
    catchAllFlow: DisciplineRecord[],
    offence: string,
    configService: ConfigService,
    contextData: Record<string, any>,
    associationEntity: AssociationSettings,
    cohort: string,
  ) => {
    const extractNextAction = (actionFlowRecords: DisciplineRecord[]) => {
      for (const actionFlowRecord of actionFlowRecords) {
        if (
          !actionFlowRecord.getUid() &&
          (actionFlowRecord.isPrescribedAction() || actionFlowRecord.getAction() === DisciplineActionsEnum.TERMINATE)
        ) {
          return actionFlowRecord.getAction()
        }
      }
    }
    if (catchAllFlow.length) {
      return extractNextAction(catchAllFlow)
    }
    if (!activeActionFlows.hasOwnProperty(offence)) {
      activeActionFlows = this.addActionFlow(
        activeActionFlows,
        offence,
        contextData,
        configService,
        associationEntity,
        cohort,
      )
    }
    return extractNextAction(activeActionFlows[offence])
  }

  submitBulkDisciplineActions = async (
    association: AssociationId,
    bulkActionTasks: BulkDisciplineActionSchema[],
    authCredentials: AuthCredentials,
  ) => {
    const { username, password } = authCredentials
    const token = await SessionService.prepareAuthTokens(username, password)
    return await putBulkDisciplineActions(association, bulkActionTasks, token)
  }

  triggerBulkDisciplineDocGeneration = async (
    association: AssociationId,
    bulkActionIds: string[],
    batchLabel: string,
    authCredentials: AuthCredentials,
  ) => {
    const { username, password } = authCredentials
    const token = await SessionService.prepareAuthTokens(username, password)
    const payload = {
      bulkActionIds,
      batchLabel,
      asyncTaskType: AsyncTaskTypesEnum.BULK_DISCIPLINE_ALL,
    }
    const response = await createNewBatch(association, payload, token)
    return response
  }

  getExpiryUnixMs = (
    associationEntity: AssociationSettings,
    profileEntity: Profile,
    configService: ConfigService,
    currentTime: number,
    offence: string,
    action: string,
  ) => {
    const expiryConfig = associationEntity.getConfiguredProcessInfo(
      "actionExpiry",
      ProcessType.discipline,
      { ...profileEntity.getGeneralData(), offence },
      configService,
      {},
    )
    return currentTime + expiryConfig[action]
  }

  static asyncTasksToDownloadQueueItems = (asyncTasks: any[]) => {
    return asyncTasks.map((item) => {
      const { batchLabel = "UNKNOWN USER", status, content } = item.payload || {}
      const date = `${dayjs.unix(item.createdAtUnixMs / 1000).format("YYYY-MM-DD,  HH:mm")}`
      const divider = `\xa0 | \xa0`
      const suffix = `${divider}${batchLabel}`
      const label = `${date} ${suffix}`
      return {
        label,
        queueId: item.sk,
        status,
        fileLocation: content || {},
      }
    })
  }
}

export { DisciplineService }
